import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { usePageById, useUpdatePage, useDeletePage } from '../../hooks/usePage';
import PageBreadcrumbs from './PageBreadcrumbs';
import {
  updatePage as updatePageThunk,
  updatePageTitleOptimistic,
  updatePageIconOptimistic,
  updatePageCoverOptimistic,
  updatePageFavoriteOptimistic,
  toggleFavorite
  // fetchPages as fetchPagesThunk
} from '../../redux/Slices/pageSlice';
import { memberInfo } from '../../redux/Slices/memberSlice';
import MemberImage from '../Global/MemberImage';
import MoreMembersCount from '../Global/MoreMembersCount';
import PageEditor from './PageEditor';
import PagePresenceBar from './PagePresenceBar';
import PageCommentsSidebar from './PageCommentsSidebar';
import { useYjsProvider } from '../../hooks/useYjsProvider';
import { generateUserColor } from '../../utils/collaborationColors';
// import PageGridItem from './PageGridItem';
// import { fetchPages } from '../../api/pageApi';

import MovePageDialog from './MovePageDialog';
import SkeletonPage from '../Skeletons/SkeletonPage';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import {
  MoreHoriz,
  Star,
  StarOutline,
  Public,
  Lock,
  Image as ImageIcon,
  Close
} from '../icons';
import { MessageSquare, CheckCheck } from 'lucide-react';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import './scss/PageView.scss';
import { uploadCoverImage } from '../../api/pageApi';
import { config } from '../../config';
import {
  canEdit,
  canManageMembers,
  canDelete,
  canMove,
  canDuplicate,
  canFavorite,
  getRoleDisplayName,
  getRoleDescription
} from '../../utils/pagePermissions';
import Heart from '../icons/Heart';
import { useSocketAction, useSocketEvent } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import socketManager from '../../utils/SocketManager';
import {
  toggleCommentsSidebar,
  toggleShowResolved,
  addCommentFromSocket,
  updateCommentFromSocket,
  deleteCommentFromSocket,
  addReplyFromSocket,
} from '../../redux/Slices/pageCommentsSlice';


/**
 * Page View Component
 * Uses Notion-style page system with permissions
 */
const PageView = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const [isRepositioningCover, setIsRepositioningCover] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [coverPosition, setCoverPosition] = useState(50);
  const [tempCover, setTempCover] = useState(null);
  const [tempUrl, setTempUrl] = useState(null);
  // Use refs for values that don't need to trigger re-renders or need immediate access
  const dragStartYRef = useRef(0);
  const initialCoverPositionRef = useRef(50);
  const coverContainerRef = useRef(null);

  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [members, setMembers] = useState([]);
  const [subpages, setSubpages] = useState([]);
  const [pagePresenceUsers, setPagePresenceUsers] = useState([]);
  const [isLoadingSubpages, setIsLoadingSubpages] = useState(false);

  const pageViewRef = useRef(null);
  const titleInputRef = useRef(null);
  const lastSavedContentRef = useRef(null);
  const isSavingRef = useRef(false);
  const coverInputRef = useRef(null);
  const titleBroadcastTimerRef = useRef(null);
  const titleSaveTimerRef = useRef(null);
  const lastAppliedMetaTsRef = useRef(0);

  const { handlePopoverClick } = useHandlePopoverClick();

  const { data: hookData, isLoading, error } = usePageById(pageId);
  const updatePageMutation = useUpdatePage();
  const deletePageMutation = useDeletePage();

  // Get page from Redux store (for optimistic updates and source of truth)
  const page = useSelector(state => state.pages.byId[pageId]);
  const { user } = useSelector(state => state.user);
  const { sidebarOpen: commentsOpen, showResolved } = useSelector(state => state.pageComments);

  const userRole = page?.role || 'viewer';

  // Derived states from Redux page
  const title = page?.title ?? '';
  const icon = page?.icon ?? '📄';
  const rawCover = tempCover || page?.coverImage || null;
  const currentCover = rawCover?.url ? rawCover : null;

  // Sync cover position from Redux when not repositioning
  useEffect(() => {
    if (!isRepositioningCover && page?.coverImage?.position !== undefined) {
      setCoverPosition(page.coverImage.position);
    }
  }, [page?.coverImage?.position, isRepositioningCover]);

  // Reset local states when pageId changes to avoid stale data
  useEffect(() => {
    setTempCover(null);
    setTempUrl(null);
    setMembers([]);
    setSubpages([]);
    setPagePresenceUsers([]);
    lastSavedContentRef.current = null;
    isSavingRef.current = false;
    setSaveStatus('saved');
    setIsRepositioningCover(false);
  }, [pageId]);

  // Compute isFavorite from favorite array or use isFavorite field if available
  const isFavorite = page?.isFavorite ?? (page?.favorite?.includes(user?.user?.id) || false);

  // Sync last saved content for the editor
  useEffect(() => {
    if (page && !lastSavedContentRef.current && page.content) {
      lastSavedContentRef.current = JSON.stringify(page.content);
    }
  }, [page]);

  // Update browser tab title when title changes
  useEffect(() => {
    if (title) {
      document.title = `${title} - Zoobbe`;
    }

    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = 'Zoobbe - Plan. Collaborate. Grow.';
    };
  }, [title]);

  // Update browser tab favicon dynamically
  useEffect(() => {
    if (page?.icon) {
      const canvas = document.createElement('canvas');
      canvas.height = 32;
      canvas.width = 32;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = '28px serif';
        ctx.fillText(page.icon, 0, 26);
        const faviconUrl = canvas.toDataURL();

        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }

    return () => {
      // Optional: reset favicon on unmount
      const link = document.querySelector("link[rel*='icon']");
      if (link) link.href = '/favicon.ico';
    };
  }, [page?.icon]);

  // Fetch members when page loads
  useEffect(() => {
    const fetchMembers = async () => {
      if (!pageId) return;

      try {
        const response = await fetch(`${config.API_URI}/v1/pages/${pageId}/members`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setMembers(data.members || []);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
      }
    };

    fetchMembers();
  }, [pageId]);

  // Fetch subpages - Disabled for now
  /*
  useEffect(() => {
    const loadSubpages = async () => {
      if (!pageId) return;

      setIsLoadingSubpages(true);
      try {
        // Need to get the actual mongo _id for parentId filtering
        // data.page._id is the mongo ID, pageId is the shortId
        if (page?._id) {
          const result = await fetchPages({ parentId: page._id });
          setSubpages(result?.pages || []);
        }
      } catch (error) {
        console.error('Error fetching subpages:', error);
      } finally {
        setIsLoadingSubpages(false);
      }
    };

    if (page?._id) {
      loadSubpages();
    }
  }, [page?._id]);
  */

  // Save title to API (shared by blur and auto-save)
  const saveTitle = async (newTitle) => {
    if (newTitle === page.title) return;

    // Broadcast via Yjs awareness for real-time sync
    if (awareness) {
      awareness.setLocalStateField('pageMetaUpdate', { title: newTitle, ts: Date.now() });
    }

    // Dispatch optimistic update to Redux
    dispatch(updatePageTitleOptimistic({ pageId: page.shortId, title: newTitle }));
    document.title = `${newTitle} - Zoobbe`;

    try {
      setSaveStatus('saving');
      await dispatch(updatePageThunk({ pageId: page.shortId, updates: { title: newTitle } })).unwrap();
      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update title:', error);
      setSaveStatus('error');
    }
  };

  const handleTitleBlur = () => {
    // Clear pending auto-save since we're saving now
    if (titleSaveTimerRef.current) {
      clearTimeout(titleSaveTimerRef.current);
      titleSaveTimerRef.current = null;
    }
    const currentText = titleInputRef.current?.textContent?.trim() || '';
    const newTitle = currentText || 'Untitled';
    saveTitle(newTitle);
  };

  // Broadcast title changes live + auto-save after 2s of inactivity
  const handleTitleInput = () => {
    const currentText = titleInputRef.current?.textContent?.trim() || 'Untitled';

    // Broadcast via Yjs awareness (300ms debounce)
    if (awareness) {
      if (titleBroadcastTimerRef.current) clearTimeout(titleBroadcastTimerRef.current);
      titleBroadcastTimerRef.current = setTimeout(() => {
        awareness.setLocalStateField('pageMetaUpdate', { title: currentText, ts: Date.now() });
      }, 300);
    }

    // Auto-save to API (2s debounce)
    if (titleSaveTimerRef.current) clearTimeout(titleSaveTimerRef.current);
    titleSaveTimerRef.current = setTimeout(() => {
      const text = titleInputRef.current?.textContent?.trim() || 'Untitled';
      saveTitle(text);
    }, 2000);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      titleInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (titleInputRef.current) {
        titleInputRef.current.textContent = page.title;
        titleInputRef.current.blur();
      }
    }
  };

  const handleTitlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') || '';
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text.replace(/\n/g, ' ')));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    handlePopoverClick(e, 'emojiPicker', {
      onEmojiSelect: handleEmojiSelect,
      isClosed: false
    });
  };

  const handleEmojiSelect = async (emojiData) => {
    const newIcon = emojiData.native;

    // Broadcast via Yjs awareness for real-time sync
    if (awareness) {
      awareness.setLocalStateField('pageMetaUpdate', { icon: newIcon, ts: Date.now() });
    }

    // Dispatch optimistic update to Redux
    dispatch(updatePageIconOptimistic({ pageId: page.shortId, icon: newIcon }));

    try {
      // Make API call
      await dispatch(updatePageThunk({ pageId: page.shortId, updates: { icon: newIcon } })).unwrap();
    } catch (error) {
      console.error('Failed to update icon:', error);
    }
  };

  const handleContentChange = async (content) => {
    const contentString = JSON.stringify(content);
    if (isSavingRef.current || contentString === lastSavedContentRef.current) {
      return;
    }

    // Guard: never overwrite real content with a default empty editor state
    const children = content?.root?.children;
    const isEmptyContent = !children || children.length === 0 ||
      (children.length === 1 && children[0].type === 'paragraph' &&
       (!children[0].children || children[0].children.length === 0));
    if (isEmptyContent && lastSavedContentRef.current) {
      return;
    }

    try {
      // In collaborative mode, save silently (periodic background save to keep Page.content in sync)
      if (!isCollaborative) {
        setSaveStatus('saving');
      }
      isSavingRef.current = true;
      lastSavedContentRef.current = contentString;

      await updatePageMutation.mutateAsync({
        pageId: page.shortId,
        updates: { content }
      });
      if (!isCollaborative) {
        setSaveStatus('saved');
      }
    } catch (error) {
      console.error('Failed to update content:', error);
      if (!isCollaborative) {
        setSaveStatus('error');
      }
      lastSavedContentRef.current = null;
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleDeletePage = async () => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      await deletePageMutation.mutateAsync(page.shortId);
      navigate(-1);
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const handleToggleFavorite = async () => {
    const newFavoriteStatus = !isFavorite;
    const userId = user?.user?.id;

    // Dispatch optimistic update to Redux
    dispatch(updatePageFavoriteOptimistic({
      pageId: page.shortId,
      isFavorite: newFavoriteStatus,
      userId
    }));

    try {
      // Make API call using the correct toggleFavorite thunk
      await dispatch(toggleFavorite({ pageId: page.shortId })).unwrap();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Rollback on error
      dispatch(updatePageFavoriteOptimistic({
        pageId: page.shortId,
        isFavorite: !newFavoriteStatus,
        userId
      }));
    }
  };

  const handleCoverUploadClick = () => {
    coverInputRef.current?.click();
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    // Create temporary preview URL
    const url = URL.createObjectURL(file);
    const mockCover = {
      url,
      position: 50,
      isTemporary: true
    };

    setTempUrl(url);
    setTempCover(mockCover);

    // Dispatch optimistic update to Redux
    dispatch(updatePageCoverOptimistic({ pageId: page.shortId, coverImage: mockCover }));

    try {
      const result = await uploadCoverImage(page.shortId, file);
      const newCover = result?.coverImage || null;

      // Clean up temporary URL
      URL.revokeObjectURL(tempUrl);

      if (newCover) {
        setTempCover(null);
        setTempUrl(null);
        dispatch(updatePageCoverOptimistic({ pageId: page.shortId, coverImage: newCover }));
      }
    } catch (error) {
      console.error('Failed to upload cover image:', error);
      alert('Failed to upload cover image');

      // Rollback
      URL.revokeObjectURL(tempUrl);
      dispatch(updatePageCoverOptimistic({ pageId: page.shortId, coverImage: page.coverImage || null }));
    } finally {
      e.target.value = '';
    }
  };

  const handleRemoveCover = async () => {
    // Broadcast via Yjs awareness for real-time sync
    if (awareness) {
      awareness.setLocalStateField('pageMetaUpdate', { coverImage: null, ts: Date.now() });
    }

    // Dispatch optimistic update to Redux
    dispatch(updatePageCoverOptimistic({ pageId: page.shortId, coverImage: null }));

    try {
      // Make API call
      await dispatch(updatePageThunk({ pageId: page.shortId, updates: { coverImage: null } })).unwrap();
    } catch (error) {
      console.error('Failed to remove cover:', error);
    }
  };

  const handleRepositionCover = () => {
    setIsRepositioningCover(true);
    // Initialize ref with current position
    initialCoverPositionRef.current = page?.coverImage?.position || 50;
    // For local UI dragging, we can use a temporary state if needed,
    // but coverPosition is already initialized from page.coverImage.position in the derived state.
  };

  const handleMouseDown = (e) => {
    if (!isRepositioningCover) return;

    e.preventDefault(); // Prevent text selection
    setIsDragging(true);

    dragStartYRef.current = e.clientY;
    initialCoverPositionRef.current = coverPosition;

    document.body.style.cursor = 'grabbing';
  };

  // Effect to handle global mouse move and up events
  useEffect(() => {
    const handleWindowMouseMove = (e) => {
      if (!isDragging || !isRepositioningCover || !coverContainerRef.current) return;

      e.preventDefault();

      const deltaY = e.clientY - dragStartYRef.current;
      const containerHeight = coverContainerRef.current.offsetHeight;

      // Sensitivity factor
      const sensitivity = 0.25;

      // Calculate percentage change
      const percentageChange = (deltaY / containerHeight) * 100 * sensitivity;

      let newPosition = initialCoverPositionRef.current - percentageChange;

      // Clamp between 0 and 100
      newPosition = Math.max(0, Math.min(100, newPosition));

      setCoverPosition(newPosition);
    };

    const handleWindowMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        document.body.style.cursor = '';
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleWindowMouseMove);
      window.addEventListener('mouseup', handleWindowMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isDragging, isRepositioningCover]);

  const handleSaveCoverPosition = async () => {
    const baseCover = page?.coverImage || {};
    const newCover = { ...baseCover, position: coverPosition };

    setIsRepositioningCover(false);
    setIsDragging(false);
    document.body.style.cursor = '';

    // Dispatch optimistic update to Redux
    dispatch(updatePageCoverOptimistic({ pageId: page.shortId, coverImage: newCover }));

    try {
      // Make API call
      await dispatch(updatePageThunk({ pageId: page.shortId, updates: { coverImage: newCover } })).unwrap();
    } catch (error) {
      console.error('Failed to update cover position:', error);
      // Redux will handle rollback or we can re-fetch
      setIsRepositioningCover(true);
    }
  };

  const handleCancelReposition = () => {
    setIsRepositioningCover(false);
    setIsDragging(false);
    document.body.style.cursor = '';
  };

  // Permission checks using utility functions
  const canEditPage = canEdit(userRole);
  const canDeletePage = canDelete(userRole);
  const canSharePage = canManageMembers(userRole); // Only owners can manage members/sharing
  const canMovePage = canMove(userRole);
  const canDuplicatePage = canDuplicate(userRole);
  const canFavoritePage = canFavorite(userRole);

  // Collaborative editing via Yjs (only for editors)
  const { ready: collabReady, providerFactory, awareness, connectionStatus } = useYjsProvider(
    pageId,
    { enabled: canEditPage, user: user?.user }
  );

  // Collaborative mode requires BOTH a token (collabReady) AND a live WS
  // connection. Gating on collabReady alone was a data-loss trap: the token
  // fetch succeeds over HTTP and flips collabReady=true even when the Yjs
  // WebSocket never connects ("Reconnecting…"). In that state the editor ran in
  // collaborative mode, which DISABLES the per-keystroke HTTP save (only a 30s
  // periodic save remains) AND the WS sync is dead — so typed content went
  // nowhere. Requiring connectionStatus==='connected' means a dead socket falls
  // back to non-collaborative mode, where every change is saved over HTTP.
  const isCollaborative = canEditPage && collabReady && connectionStatus === 'connected';
  const currentUser = user?.user ? {
    id: user.user._id || user.user.id,
    name: user.user.name || user.user.username,
    color: generateUserColor(user.user._id || user.user.id),
    picture: user.user.profilePicture || null,
  } : null;

  // Build the join-page payload for the current user/page/role.
  const buildPresenceData = useCallback(() => {
    if (!pageId || !user?.user) return null;
    const userId = user.user._id || user.user.id;
    return {
      pageId,
      userId,
      name: user.user.name || user.user.username,
      picture: user.user.profilePicture || null,
      role: userRole,
      color: generateUserColor(userId),
    };
  }, [pageId, user?.user, userRole]);

  // Page presence: emit join/leave via Socket.IO.
  useEffect(() => {
    const presenceData = buildPresenceData();
    if (!presenceData) return;

    socketManager.emit('join-page', presenceData);

    return () => {
      socketManager.emit('leave-page', { pageId });
    };
  }, [pageId, user?.user, userRole, buildPresenceData]);

  // Re-announce presence after a reconnect — the server drops our presence when
  // the socket disconnects, so without this we'd silently vanish from everyone
  // else's presence bar until navigating away and back.
  useSocketEvent('socket-connected', () => {
    const presenceData = buildPresenceData();
    if (presenceData) socketManager.emit('join-page', presenceData);
  }, [buildPresenceData]);

  // While our own socket is down, clear the presence list — the data is stale
  // (we can't receive leave updates), so showing it would falsely imply others
  // are still here. It repopulates on reconnect from the fresh broadcast.
  useSocketEvent('socket-disconnected', () => {
    setPagePresenceUsers([]);
  }, []);

  // Listen for page presence updates
  useSocketEvent('page-presence-update', (data) => {
    if (data.pageId !== pageId) return;
    const currentUserId = user?.user?._id || user?.user?.id;
    // Filter out self from presence list
    const others = (data.users || []).filter(u => u.userId !== currentUserId);
    setPagePresenceUsers(others);
  }, [pageId, user?.user?._id, user?.user?.id]);

  // Real-time sync for page metadata via Yjs awareness (title, icon, cover)
  useEffect(() => {
    if (!awareness) return;

    const handleAwarenessUpdate = ({ added, updated }) => {
      const clients = [...(added || []), ...(updated || [])];

      for (const clientId of clients) {
        if (clientId === awareness.clientID) continue; // Skip self

        const state = awareness.getStates().get(clientId);
        if (!state?.pageMetaUpdate) continue;

        const meta = state.pageMetaUpdate;
        if (!meta.ts || meta.ts <= lastAppliedMetaTsRef.current) continue;

        lastAppliedMetaTsRef.current = meta.ts;

        if (meta.title !== undefined) {
          dispatch(updatePageTitleOptimistic({ pageId, title: meta.title }));
          if (titleInputRef.current && document.activeElement !== titleInputRef.current) {
            titleInputRef.current.textContent = meta.title;
          }
          document.title = `${meta.title} - Zoobbe`;
        }
        if (meta.icon !== undefined) {
          dispatch(updatePageIconOptimistic({ pageId, icon: meta.icon }));
        }
        if (meta.coverImage !== undefined) {
          dispatch(updatePageCoverOptimistic({ pageId, coverImage: meta.coverImage }));
        }
      }
    };

    awareness.on('update', handleAwarenessUpdate);
    return () => awareness.off('update', handleAwarenessUpdate);
  }, [awareness, dispatch, pageId]);

  // Fallback: Socket.IO sync for metadata (works when backend emits PAGE_UPDATED with fields)
  useSocketAction(ActionTypes.PAGE_UPDATED, (data) => {
    if (data.pageId !== pageId) return;
    const currentUserId = user?.user?._id || user?.user?.id;
    if (data.reqUser === currentUserId) return;

    if (data.page) {
      dispatch({ type: 'pages/updatePageFromSocket', payload: data.page });

      if (data.page.title !== undefined && titleInputRef.current) {
        if (document.activeElement !== titleInputRef.current) {
          titleInputRef.current.textContent = data.page.title;
        }
      }
    }
  }, [pageId, user?.user?._id, user?.user?.id]);

  // Real-time comment sync via Socket.IO
  const currentUserId = user?.user?._id || user?.user?.id;

  useSocketAction(ActionTypes.PAGE_COMMENT_ADDED, (data) => {
    if (data.pageId !== pageId) return;
    if (data.reqUser === currentUserId) return;
    dispatch(addCommentFromSocket({ pageId: data.pageId, comment: data.comment }));
  }, [pageId, currentUserId]);

  useSocketAction(ActionTypes.PAGE_COMMENT_UPDATED, (data) => {
    if (data.pageId !== pageId) return;
    if (data.reqUser === currentUserId) return;
    dispatch(updateCommentFromSocket({ pageId: data.pageId, comment: data.comment }));
  }, [pageId, currentUserId]);

  useSocketAction(ActionTypes.PAGE_COMMENT_DELETED, (data) => {
    if (data.pageId !== pageId) return;
    if (data.reqUser === currentUserId) return;
    dispatch(deleteCommentFromSocket({ pageId: data.pageId, commentId: data.commentId }));
  }, [pageId, currentUserId]);

  useSocketAction(ActionTypes.PAGE_COMMENT_REPLY_ADDED, (data) => {
    if (data.pageId !== pageId) return;
    if (data.reqUser === currentUserId) return;
    dispatch(addReplyFromSocket({
      pageId: data.pageId,
      parentCommentId: data.parentCommentId,
      reply: data.reply
    }));
  }, [pageId, currentUserId]);

  useSocketAction(ActionTypes.PAGE_COMMENT_RESOLVED, (data) => {
    if (data.pageId !== pageId) return;
    if (data.reqUser === currentUserId) return;
    dispatch(updateCommentFromSocket({ pageId: data.pageId, comment: data.comment }));
  }, [pageId, currentUserId]);

  // Format last edited time (Notion-style)
  const formatLastEdited = (date) => {
    if (!date) return '';
    const now = new Date();
    const edited = new Date(date);
    const diffMs = now - edited;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Edited just now';
    if (diffMins < 60) return `Edited ${diffMins}m ago`;
    if (diffHours < 24) return `Edited ${diffHours}h ago`;
    if (diffDays < 7) return `Edited ${diffDays}d ago`;

    return `Edited ${edited.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  // Show skeleton if loading and no cached data from Redux
  if (isLoading && !page) {
    return <SkeletonPage />;
  }

  // Only show error if no cached data available
  if (error && !page) {
    return (
      <div className="page-error">
        <h2>Failed to load page</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  // Handle case where page not found after loading finished
  if (!page && !isLoading) {
    return (
      <div className="page-error">
        <h2>Page not found</h2>
        <p>The page you're looking for doesn't exist or you don't have access.</p>
        <button onClick={() => navigate('/pages')} className="back-btn">
          Go to Pages
        </button>
      </div>
    );
  }


  return (
    <div className={`page-view ${currentCover ? 'has-cover' : 'no-cover'}`} ref={pageViewRef}>
      {/* Top Navigation Bar */}
      <div className="page-top-bar">
        <div className="page-top-bar-left">
          {/* Page icon and title (Notion-style) */}
          <div className="page-top-info">
            <span className="page-top-icon">{icon}</span>
            <h1 className="page-top-title">{title || 'Untitled'}</h1>
          </div>

          {/* Privacy indicator */}
          {page.isPublic ? (
            <div
              className="page-top-badge public"
              title="This page is shared to the web"
              onClick={(e) => handlePopoverClick(e, 'sharePageMembers', { page, isOwner: userRole === 'owner', isClosed: true })}
            >
              <Public size={14} />
              <span>Public</span>
            </div>
          ) : (
            <div
              className="page-top-badge private"
              title="This page is private to you and invited members"
              onClick={(e) => handlePopoverClick(e, 'sharePageMembers', { page, isOwner: userRole === 'owner', isClosed: true })}
            >
              <Lock size={14} />
              <span>Private</span>
            </div>
          )}
        </div>
        <div className="page-top-bar-center">
          {saveStatus === 'saving' && (
            <span className="page-save-status saving">
              Saving...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="page-save-status saved">
              Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="page-save-status error">
              Save failed
            </span>
          )}
          {page?.updatedAt && saveStatus === 'saved' && (
            <span className="page-last-edited">
              {formatLastEdited(page.updatedAt)}
            </span>
          )}
          {isCollaborative && connectionStatus !== 'connected' && (
            <span className="collab-status" title="Trying to reconnect to the collaboration server. Your changes are saved locally and will sync when reconnected.">
              <span className="collab-status-dot reconnecting" />
              Reconnecting…
            </span>
          )}
          {isCollaborative && connectionStatus === 'connected' && pagePresenceUsers.some(u => u.role === 'editor' || u.role === 'owner') && (
            <span className="collab-status">
              <span className="collab-status-dot connected" />
              Collaborating
            </span>
          )}
        </div>
        <div className="page-top-bar-right">
          {/* Permission level indicator */}
          {userRole && userRole !== 'owner' && (
            <div className="page-permission-indicator" title={getRoleDescription(userRole)}>
              <span className="permission-icon">
                {userRole === 'viewer' && '👁️'}
                {userRole === 'commenter' && '💬'}
                {userRole === 'editor' && '✏️'}
              </span>
              <span className="permission-text">{getRoleDisplayName(userRole)}</span>
            </div>
          )}

          {/* Active collaborators / viewers */}
          {pagePresenceUsers.length > 0 && (
            <PagePresenceBar users={pagePresenceUsers} />
          )}

          {/* Members avatars */}
          {members.length > 0 && (
            <div className="page-members-avatars">
              <MemberImage
                members={members.map(m => m.user).filter(Boolean).slice(0, 3)}
                type="page"
                size={28}
                displayMembersLimit={3}
                uniqId={page.shortId}
              />
              <MoreMembersCount
                members={members.map(m => m.user).filter(Boolean)}
                boardId={page.shortId}
                uniqid={page.shortId}
              />
            </div>
          )}

          {/* Share button - Only owners can manage members */}
          {canSharePage && (
            <button
              id="page-share-button"
              className="page-top-action-btn page-share-btn"
              title="Share page"
              data-popover-trigger
              onClick={(e) => handlePopoverClick(e, 'sharePageMembers', { page, isOwner: userRole === 'owner', isClosed: true })}
            >
              Share
            </button>
          )}

          {/* Comments toggle */}
          <button
            className={`page-top-action-btn page-comments-btn ${commentsOpen ? 'active' : ''}`}
            onClick={() => dispatch(toggleCommentsSidebar())}
            title="Toggle comments"
          >
            <MessageSquare size={18} />
          </button>

          {/* Show/hide resolved comments — only visible when comments panel is open */}
          {commentsOpen && (
            <button
              className={`page-top-action-btn ${showResolved ? 'active' : ''}`}
              onClick={() => dispatch(toggleShowResolved())}
              title={showResolved ? 'Hide resolved' : 'Show resolved'}
            >
              <CheckCheck size={18} />
            </button>
          )}

          {/* Favorite toggle */}
          <button
            className="page-top-action-btn page-favorite-btn"
            onClick={handleToggleFavorite}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorite ? (
              <Heart size={20} color="red" />
            ) : (
              <Heart size={20} />
            )}
          </button>

          {/* More menu */}
          <button
            id="page-actions-menu-button"
            className="page-top-action-btn"
            title="More actions"
            data-popover-trigger
            onClick={(e) => handlePopoverClick(e, 'pageActionsMenu', {
              page,
              workspaceId: page.workspaceId?.shortId || page.workspaceId,
              onMove: () => setShowMoveDialog(true),
              isClosed: false
            })}
          >
            <MoreHoriz size={20} />
          </button>
        </div>
      </div>

      <PageBreadcrumbs page={page} />

      {/* Cover Image */}
      {currentCover && (
        <div
          className="page-cover-container"
          ref={coverContainerRef}
        >
          <div
            className={`page-cover ${isRepositioningCover ? 'repositioning' : ''}`}
            style={{
              backgroundImage: `url("${currentCover.url}")`,
              backgroundSize: 'cover',
              backgroundPosition: `center ${isRepositioningCover ? coverPosition : (currentCover.position || 50)}%`,
              cursor: isRepositioningCover ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onMouseDown={isRepositioningCover ? handleMouseDown : undefined}
          />
          {/* Cover controls - Only for editors and NOT when repositioning */}
          {canEditPage && !isRepositioningCover && (
            <div className="page-cover-controls">
              <button
                className="page-cover-control-btn"
                onClick={handleRepositionCover}
                title="Reposition"
              >
                Reposition
              </button>
              <button
                className="page-cover-control-btn"
                onClick={handleRemoveCover}
                title="Remove cover"
              >
                <Close size={16} />
              </button>
            </div>
          )}
          {isRepositioningCover && canEditPage && (
            <div className="page-cover-reposition-overlay">
              <div className="page-cover-reposition-text">
                Drag to reposition
              </div>
              <div className="page-cover-reposition-actions">
                <button onClick={handleSaveCoverPosition} className="page-cover-save-btn">
                  Save position
                </button>
                <button onClick={handleCancelReposition} className="page-cover-cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Page Content */}
      <div className="page-content">
        {/* Icon */}
        <button
          id="page-icon-picker-button"
          className="page-icon-large"
          onClick={canEditPage ? handleIconClick : undefined}
          title={canEditPage ? "Change icon" : "Icon (read-only)"}
          disabled={!canEditPage}
          style={{ cursor: canEditPage ? 'pointer' : 'default' }}
        >
          {icon}
        </button>

        {/* Add cover and comment buttons - Only for editors */}
        {canEditPage && (
          <div className="page-meta-actions">
            {!currentCover && (
              <>
                <button className="page-meta-action-btn" onClick={handleCoverUploadClick}>
                  <ImageIcon size={14} />
                  Add cover
                </button>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>
        )}

        {/* Title */}
        <h1
          ref={titleInputRef}
          className={`page-title ${!canEditPage ? 'read-only' : ''}`}
          contentEditable={canEditPage}
          suppressContentEditableWarning={true}
          onBlur={canEditPage ? handleTitleBlur : undefined}
          onInput={canEditPage ? handleTitleInput : undefined}
          onKeyDown={canEditPage ? handleTitleKeyDown : undefined}
          onPaste={canEditPage ? handleTitlePaste : undefined}
          spellCheck={false}
          data-placeholder={canEditPage ? "Untitled" : ""}
          data-empty={(!title || title.trim() === '') ? 'true' : undefined}
        >
          {title}
        </h1>

        {/* Editor – stable key prevents remount (and blank flash) during collab transition */}
        <PageEditor
          key={pageId}
          pageId={pageId}
          initialContent={page.content}
          onContentChange={handleContentChange}
          readOnly={!canEditPage}
          collaborative={isCollaborative}
          providerFactory={isCollaborative ? providerFactory : undefined}
          currentUser={currentUser}
        />

        {/* Subpages Section - Disabled for now */}
        {/* {(subpages.length > 0 || isLoadingSubpages) && (
          <div className="page-subpages-section">
            <div className="section-divider" />
            <h3 className="section-title">Subpages</h3>
            {isLoadingSubpages ? (
              <div className="subpages-loading">
                <Spinner size={24} />
              </div>
            ) : (
              <div className="subpages-grid">
                {subpages.map(subpage => (
                  <PageGridItem
                    key={subpage.shortId}
                    page={subpage}
                    workspaceId={page.workspaceId?.shortId || page.workspaceId}
                  />
                ))}
              </div>
            )}
          </div>
        )} */}

      </div>

      {/* Floating comment cards */}
      <PageCommentsSidebar pageId={pageId} userRole={userRole} scrollContainerRef={pageViewRef} />

      {/* Modals */}
      {showMoveDialog && (
        <MovePageDialog
          page={page}
          onClose={() => setShowMoveDialog(false)}
          onSuccess={() => {
            // Optionally navigate to new location
          }}
        />
      )}
    </div>
  );
};

export default PageView;

